/**
 * Scherm 02 — Houtstaat: de materiaalstaat en de tomtom van één kar.
 *
 * Dit is het scherm van productievoorbereiding. Hier worden de gegevens gecontroleerd
 * en gecorrigeerd, afwijkingen goedgekeurd, het bouwelement in karren gesplitst en
 * uiteindelijk vrijgegeven voor productie.
 *
 * Zodra "Produceren" is ingedrukt is de houtstaat vergrendeld voor wijzigingen en
 * staat het bouwelement in de planning. De cellen leggen dan uit waarom ze niet meer
 * aanpasbaar zijn; de API weigert de wijziging ook.
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  api,
  type Foutmelding,
  type KarAntwoord,
  type Keuzes,
  type AuditRegel,
  type Weergavestap,
} from '../api';
import { datumTijd, uren, useGegevens, type KarInBoom } from '../gegevens';
import { Maatlijn } from '../onderdelen/Maatlijn';
import { Bouwnummerfilter } from '../onderdelen/Bouwnummerfilter';
import { Dialoog } from '../onderdelen/Dialoog';
import { Foutcontrole } from '../onderdelen/Foutcontrole';
import {
  IconChevronOnder,
  IconKubus,
  IconPrinter,
  IconSplitsen,
  IconUpload,
} from '../onderdelen/Iconen';
import { Importdialoog, type Importbron } from '../onderdelen/Importdialoog';
import { Legenda, Materiaalstaat } from '../onderdelen/Materiaalstaat';
import { Routepaneel } from '../onderdelen/Routepaneel';
import { Stapmenu } from '../onderdelen/Stapmenu';
import { Wijzigingslog } from '../onderdelen/Wijzigingslog';

/**
 * De viewer brengt three.js mee — het grootste deel van de bundel. Hij wordt pas
 * geladen als iemand het tabblad opent, zodat de materiaalstaat snel blijft.
 */
const Model = lazy(() => import('./Model').then((m) => ({ default: m.Model })));

type Tab = 'materiaalstaat' | 'karren' | 'model' | 'wijzigingslog';

export function Houtstaat() {
  const { boot, karren, huidigeKar, kiesKar, kleuren, mag, meldFout, ververs, wijzigingsteller } =
    useGegevens();
  const { karId: karUitPad } = useParams<{ karId: string }>();
  const [antwoord, setAntwoord] = useState<KarAntwoord | null>(null);
  const [keuzes, setKeuzes] = useState<Keuzes | null>(null);
  const [tab, setTab] = useState<Tab>('materiaalstaat');
  /** Het onderdeel waarop het 3D-tabblad opent, gezet door de 3D-knop in de staat. */
  const [modelOnderdeel, setModelOnderdeel] = useState<string | null>(null);
  /** Het bouwelement dat het 3D-tabblad toont; standaard het eerste op de kar. */
  const [modelElement, setModelElement] = useState<string | null>(null);
  const [stapMenu, setStapMenu] = useState<Weergavestap | null>(null);
  const [splitsen, setSplitsen] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [importeren, setImporteren] = useState<Importbron | null>(null);
  const [log, setLog] = useState<AuditRegel[] | null>(null);
  const [bezigMelding, setBezigMelding] = useState<string | null>(null);
  const [laden, setLaden] = useState(false);

  const bouwnummer = boot?.bouwnummer;
  const karId = huidigeKar?.kar.id;

  // Staat er een karnummer in het pad, dan is dat de kar die geopend moet worden.
  useEffect(() => {
    if (karUitPad && karUitPad !== karId && karren.some((k) => k.kar.id === karUitPad)) {
      kiesKar(karUitPad);
    }
  }, [karUitPad, karId, karren, kiesKar]);

  const haalKar = useCallback(async () => {
    if (!bouwnummer || !karId) return;
    setLaden(true);
    try {
      setAntwoord(await api.kar(bouwnummer, karId));
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De kar kon niet geladen worden.');
    } finally {
      setLaden(false);
    }
  }, [bouwnummer, karId, meldFout]);

  // `wijzigingsteller` staat er bewust bij: verandert er iets aan deze boot — ook door
  // een collega — dan haalt dit scherm de kar opnieuw op.
  useEffect(() => {
    void haalKar();
  }, [haalKar, wijzigingsteller]);

  // De keuzelijsten voor materiaal en afwerking hangen aan de boot, niet aan de kar.
  useEffect(() => {
    if (!bouwnummer) return;
    let afgebroken = false;
    void api
      .keuzes(bouwnummer)
      .then((k) => {
        if (!afgebroken) setKeuzes(k);
      })
      .catch(() => undefined);
    return () => {
      afgebroken = true;
    };
  }, [bouwnummer]);

  useEffect(() => {
    if (tab !== 'wijzigingslog' || !bouwnummer || log) return;
    void api
      .audit(bouwnummer)
      .then(setLog)
      .catch((e: unknown) => meldFout(e instanceof Error ? e.message : 'Het wijzigingslog kon niet laden.'));
  }, [tab, bouwnummer, log, meldFout]);

  const kar = antwoord?.kar ?? null;
  /** De bouwelementen die op deze kar staan; de materiaalstaat is erop gegroepeerd. */
  const elementen = antwoord?.bouwelementen ?? [];
  const vergrendeld = antwoord?.vergrendeld ?? false;
  const vergrendelUitleg = kar
    ? `De houtstaat is vergrendeld: ${kar.id} (${kar.plaatsing.karNaam}) staat op ${kar.status}.`
    : '';

  const wijzigOnderdeel = async (regelId: string, wijziging: Record<string, unknown>) => {
    if (!bouwnummer) return;
    try {
      await api.onderdeelBijwerken(bouwnummer, regelId, wijziging);
      await haalKar();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De wijziging kon niet opgeslagen worden.');
    }
  };

  const goedkeuren = async (melding: Foutmelding, intrekken: boolean) => {
    if (!bouwnummer) return;
    setBezigMelding(melding.sleutel);
    try {
      await api.goedkeuren(bouwnummer, melding.onderdeelId, melding.sleutel, intrekken);
      await haalKar();
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De goedkeuring kon niet opgeslagen worden.');
    } finally {
      setBezigMelding(null);
    }
  };

  const produceren = async () => {
    if (!bouwnummer || !kar) return;
    try {
      await api.produceren(bouwnummer, kar.id);
      await Promise.all([haalKar(), ververs()]);
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'Vrijgeven voor productie lukte niet.');
    }
  };

  if (!boot) return <div className="hs-leeg">Kies eerst een boot.</div>;
  if (!antwoord || !huidigeKar || !kar) {
    return <div className="hs-leeg">{laden ? 'De houtstaat wordt geladen…' : 'Er is nog geen kar.'}</div>;
  }

  const openRood = (kar.foutmeldingen ?? []).filter((f) => f.ernst === 'rood' && !f.goedgekeurd).length;

  return (
    <>
      <header className="hs-kop">
        <div className="hs-koprij">
          <div>
            <Kruimelpad karren={karren} huidige={huidigeKar} onKies={kiesKar} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
              <h1 className="hs-koptitel">{kar.nummer}</h1>
              <span className="hs-chip hs-chip--zacht">Prioriteit {kar.prioriteit}</span>
              {kar.reworkActief ? (
                <span
                  className="hs-chip"
                  style={{
                    background: kleuren?.rework.kleur,
                    borderColor: kleuren?.rework.rand,
                    color: kleuren?.rework.tekst,
                  }}
                  title={kar.reworkActief.reden}
                >
                  Rework · aangevraagd door {kar.reworkActief.aangevraagdDoor}
                </span>
              ) : null}
              {kar.status === 'on-hold' ? (
                <span className="hs-chip hs-chip--hold" title={kar.onHold?.reden}>
                  On-hold · {kar.onHold?.door} · {datumTijd(kar.onHold?.op)}
                </span>
              ) : null}
            </div>
            <Maatlijn />
            <p className="hs-subregel">
              {kar.plaatsing.karNaam ? `${kar.plaatsing.karNaam} · ` : ''}
              {kar.onderdelen} onderdelen op deze kar ·{' '}
              {kar.stuks} stuks · v{boot.versie} · rev {boot.revisie}
              {elementen.length > 0
                ? ` · ${elementen.length} ${elementen.length === 1 ? 'bouwelement' : 'bouwelementen'} op deze kar`
                : ''}
              {kar.uren ? ` · ${uren(kar.uren.resterend)} resterend` : ''}
            </p>
            <Bouwnummerfilter />
          </div>

          <div className="hs-kopacties">
            {mag('dataInladen') ? (
              <>
                <button
                  type="button"
                  className="hs-knop"
                  onClick={() => setImporteren('TopSolid')}
                  title="Een exportbestand uit TopSolid inlezen"
                >
                  <IconUpload /> Importeren uit TopSolid
                </button>
                <button
                  type="button"
                  className="hs-knop"
                  onClick={() => setImporteren('Excel')}
                  title="Een bestaande houtstaat-werkmap inlezen"
                >
                  <IconUpload /> Excel inladen
                </button>
              </>
            ) : null}
            {mag('karrenSplitsen') ? (
              <button
                type="button"
                className="hs-knop"
                disabled={vergrendeld}
                title={vergrendeld ? vergrendelUitleg : 'De bouwelementen over meer karren verdelen'}
                onClick={() => setSplitsen(true)}
              >
                <IconSplitsen /> Kar splitsen
              </button>
            ) : null}
            {mag('exporteren') ? (
              <a
                className="hs-knop"
                href={api.exportUrl(boot.bouwnummer, 'html', kar.id)}
                target="_blank"
                rel="noreferrer"
                title="Alleen deze kar, met materiaalstaat en tomtom"
              >
                <IconPrinter /> PDF-export
              </a>
            ) : null}
            {mag('onHoldZetten') || mag('producerenVrijgeven') ? <span className="hs-scheiding" /> : null}
            {mag('onHoldZetten') ? (
              kar.status === 'on-hold' ? (
                <button
                  type="button"
                  className="hs-knop"
                  onClick={async () => {
                    try {
                      await api.onHoldOpheffen(boot.bouwnummer, kar.id);
                      await Promise.all([haalKar(), ververs()]);
                    } catch (e) {
                      meldFout(e instanceof Error ? e.message : 'On-hold opheffen lukte niet.');
                    }
                  }}
                >
                  On-hold opheffen
                </button>
              ) : (
                <button type="button" className="hs-knop hs-knop--onhold" onClick={() => setOnHold(true)}>
                  On-hold
                </button>
              )
            ) : null}
            {mag('producerenVrijgeven') && kar.status !== 'produceren' ? (
              <button
                type="button"
                className="hs-knop hs-knop--primair"
                disabled={openRood > 0}
                title={
                  openRood > 0
                    ? `Er ${openRood === 1 ? 'staat' : 'staan'} nog ${openRood} rode ${openRood === 1 ? 'waarde' : 'waarden'} open.`
                    : 'De houtstaat vergrendelen en doorzetten naar de planning'
                }
                onClick={() => void produceren()}
              >
                Produceren
              </button>
            ) : null}
            {kar.status === 'produceren' ? (
              <span className="hs-chip hs-chip--zacht">
                In productie
              </span>
            ) : null}
          </div>
        </div>

        <div className="hs-tabs">
          <button
            type="button"
            className={`hs-tab${tab === 'materiaalstaat' ? ' actief' : ''}`}
            onClick={() => setTab('materiaalstaat')}
          >
            Materiaalstaat &amp; tomtom
          </button>
          <button
            type="button"
            className={`hs-tab${tab === 'karren' ? ' actief' : ''}`}
            onClick={() => setTab('karren')}
          >
            Karren <span className="hs-chip hs-chip--zacht">{antwoord.karrenInRuimte.length}</span>
          </button>
          {mag('modelBekijken') ? (
            <button
              type="button"
              className={`hs-tab${tab === 'model' ? ' actief' : ''}`}
              onClick={() => {
                setModelOnderdeel(null);
                setTab('model');
              }}
              title="Het 3D-model van de bouwelementen op deze kar"
            >
              <IconKubus maat={13} /> 3D-model
            </button>
          ) : null}
          <button
            type="button"
            className={`hs-tab${tab === 'wijzigingslog' ? ' actief' : ''}`}
            onClick={() => setTab('wijzigingslog')}
          >
            Wijzigingslog
          </button>
        </div>
      </header>

      <div className="hs-scherm">
        {vergrendeld ? (
          <div className="hs-melding hs-melding--info">
            {vergrendelUitleg} Alleen stappen die nog niet gestart of afgerond zijn mogen nog van plek
            wisselen.
          </div>
        ) : null}

        {tab === 'materiaalstaat' ? (
          <>
            {kar.foutmeldingen ? (
              <Foutcontrole
                meldingen={kar.foutmeldingen}
                magGoedkeuren={mag('rodeWaardenGoedkeuren')}
                onGoedkeuren={(melding, intrekken) => void goedkeuren(melding, intrekken)}
                bezig={bezigMelding}
              />
            ) : null}

            <section className="hs-paneel">
              <div className="hs-paneel__kop">
                <span className="hs-sectiekop">Route van deze kar</span>
                <span className="hs-muted">
                  {kar.route.length} stappen · klik een stap voor opties en informatie
                </span>
                {kar.uren ? (
                  <span className="hs-chip hs-chip--zacht" style={{ marginLeft: 'auto' }}>
                    {uren(kar.uren.resterend)} resterend
                  </span>
                ) : null}
              </div>
              <div className="hs-paneel__inhoud" style={{ padding: 10 }}>
                <Routepaneel
                  route={kar.route}
                  positie={kar.positie}
                  metUren={mag('urenZien')}
                  onStapKiezen={setStapMenu}
                />
              </div>
            </section>

            <section className="hs-paneel">
              <div className="hs-paneel__kop">
                <span className="hs-sectiekop">Materiaalstaat</span>
                <span className="hs-muted">
                  netto maten ·{' '}
                  {mag('gegevensAanpassen') && !vergrendeld
                    ? 'klik een cel om aan te passen · blauw = overmaat voor karwei'
                    : 'alleen lezen'}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <Materiaalstaat
                  regels={kar.materiaalstaat}
                  bouwelementen={kar.bouwelementen}
                  keuzes={keuzes}
                  magAanpassen={mag('gegevensAanpassen')}
                  vergrendeld={vergrendeld}
                  vergrendelUitleg={vergrendelUitleg}
                  onWijzigen={(id, wijziging) => void wijzigOnderdeel(id, wijziging)}
                  onModel={
                    mag('modelBekijken')
                      ? (regelId) => {
                          setModelOnderdeel(regelId);
                          setTab('model');
                        }
                      : undefined
                  }
                />
              </div>
              {kleuren ? (
                <Legenda
                  afdelingen={kleuren.afdelingen}
                  rework={kleuren.rework}
                  bewerkingen={kleuren.bewerkingen}
                />
              ) : null}
            </section>
          </>
        ) : null}

        {tab === 'karren' ? (
          <section className="hs-paneel">
            <div className="hs-paneel__kop">
              <span className="hs-sectiekop">
                Karren in <span className="hs-mono">{kar.plaatsing.ruimte}</span>{' '}
                <span className="hs-muted">{kar.plaatsing.ruimteNaam}</span>
              </span>
              <span className="hs-muted">elke kar heeft een eigen route en houtstaat</span>
            </div>
            <table className="hs-tabel hs-tabel--ruim">
              <thead>
                <tr>
                  <th>Kar</th>
                  <th>Nummer</th>
                  <th>Staat bij</th>
                  <th>Voortgang</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {antwoord.karrenInRuimte.map((k) => (
                  <tr key={k.id}>
                    <td className="hs-mono" style={{ fontWeight: 600 }}>
                      {k.kar}
                    </td>
                    <td className="hs-mono">{k.id}</td>
                    <td>
                      <span className="hs-chip">{k.afdeling}</span>
                      {k.rework ? (
                        <span
                          className="hs-chip"
                          style={{
                            marginLeft: 5,
                            background: kleuren?.rework.kleur,
                            borderColor: kleuren?.rework.rand,
                            color: kleuren?.rework.tekst,
                          }}
                        >
                          Rework
                        </span>
                      ) : null}
                    </td>
                    <td className="hs-mono">
                      {Math.min(k.positie, k.stappen)}/{k.stappen}
                    </td>
                    <td className="hs-rechts">
                      {k.id === kar.id ? (
                        <span className="hs-chip hs-chip--zacht">open</span>
                      ) : (
                        <button type="button" className="hs-knop" onClick={() => kiesKar(k.id)}>
                          Openen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        {/*
         * Het 3D-model is een tabblad zoals de andere, en geen eigen scherm: anders
         * verdwijnt de kop met het kruimelpad en de tabs zodra iemand het model opent.
         * Het scherm `/model/:id` blijft bestaan voor rechtstreekse links; dit is
         * hetzelfde onderdeel, zonder zijn eigen kop.
         */}
        {tab === 'model' ? (
          <Suspense fallback={<div className="hs-leeg">De 3D-viewer wordt geladen…</div>}>
            <Model
              bouwelementId={modelElement ?? elementen[0]?.id}
              onderdeelId={modelOnderdeel ?? undefined}
              metKop={false}
            />
          </Suspense>
        ) : null}

        {tab === 'wijzigingslog' ? (
          <section className="hs-paneel">
            <div className="hs-paneel__kop">
              <span className="hs-sectiekop">Wijzigingslog</span>
              <span className="hs-muted">wie, wat en wanneer — klik een regel voor de details</span>
            </div>
            {log === null ? (
              <div className="hs-leeg">Het wijzigingslog wordt geladen…</div>
            ) : (
              <Wijzigingslog
                regels={log}
                leegtekst="Er zijn nog geen wijzigingen vastgelegd."
              />
            )}
          </section>
        ) : null}
      </div>

      {stapMenu ? (
        <Stapmenu
          bouwnummer={boot.bouwnummer}
          karId={kar.id}
          stap={stapMenu}
          onSluiten={() => setStapMenu(null)}
          onGewijzigd={() => {
            void haalKar();
            setStapMenu(null);
          }}
        />
      ) : null}

      {splitsen ? (
        <Splitsdialoog
          bouwnummer={boot.bouwnummer}
          karId={kar.id}
          karNaam={`${kar.id} · ${kar.plaatsing.karNaam}`}
          elementen={kar.bouwelementen}
          onSluiten={() => setSplitsen(false)}
          onKlaar={async () => {
            setSplitsen(false);
            await Promise.all([ververs(), haalKar()]);
          }}
        />
      ) : null}

      {importeren ? (
        <Importdialoog
          bron={importeren}
          doel={{
            bouwnummer: boot.bouwnummer,
            deck: huidigeKar.deck,
            sectie: huidigeKar.sectie,
            ruimte: huidigeKar.ruimte,
            ruimteNaam: huidigeKar.ruimteNaam,
            kar: kar.plaatsing.kar,
            karNaam: kar.plaatsing.karNaam,
            bouwelement: elementen[0]?.plaatsing.bouwelement ?? '01',
            bouwelementNaam: elementen[0]?.plaatsing.bouwelementNaam ?? '',
          }}
          onSluiten={() => setImporteren(null)}
          onKlaar={async () => {
            setImporteren(null);
            await Promise.all([ververs(), haalKar()]);
          }}
        />
      ) : null}

      {onHold ? (
        <OnHolddialoog
          bouwnummer={boot.bouwnummer}
          karId={kar.id}
          karNaam={`${kar.id} · ${kar.plaatsing.karNaam}`}
          onSluiten={() => setOnHold(false)}
          onKlaar={async () => {
            setOnHold(false);
            await Promise.all([ververs(), haalKar()]);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Het kruimelpad `0719 / 25 Owner's deck / 6504 Owner's cabin / 10 O-01 / 01 kar 2401`.
 *
 * Elk segment is een keuzemenu, zodat je van hier naar elke andere kar in de boot kunt
 * springen zonder eerst terug naar een overzicht.
 */
function Kruimelpad({
  karren,
  huidige,
  onKies,
}: {
  karren: KarInBoom[];
  huidige: KarInBoom;
  onKies: (id: string) => void;
}) {
  const [open, setOpen] = useState<null | 'deck' | 'sectie' | 'ruimte' | 'kar'>(null);

  // Per niveau de keuzes die bij de huidige selectie passen.
  const decks = [...new Map(karren.map((k) => [k.deck, k])).values()];
  const secties = [
    ...new Map(karren.filter((k) => k.deck === huidige.deck).map((k) => [k.sectie, k])).values(),
  ];
  const ruimtes = [
    ...new Map(
      karren
        .filter((k) => k.deck === huidige.deck && k.sectie === huidige.sectie)
        .map((k) => [k.ruimte, k]),
    ).values(),
  ];
  const karrenHier = karren.filter(
    (k) => k.sectie === huidige.sectie && k.ruimte === huidige.ruimte,
  );

  /*
   * Het pad volgt de boom: deck, sectie, ruimte, kar. De kar heet naar zijn volledige
   * nummer — dat staat op de kar, op de tekening en in TopSolid — met de karnaam
   * erachter. Wat op de kar staat, de bouwelementen, staat in de materiaalstaat.
   */
  const alleNiveaus = [
    {
      sleutel: 'deck' as const,
      tekst: huidige.deck,
      keuzes: decks,
      label: (k: KarInBoom) => k.deck,
    },
    {
      sleutel: 'sectie' as const,
      tekst: `sectie ${huidige.sectie}`,
      keuzes: secties,
      label: (k: KarInBoom) => `sectie ${k.sectie}`,
    },
    {
      sleutel: 'ruimte' as const,
      tekst: `${huidige.ruimte} ${huidige.ruimteNaam}`.trim(),
      keuzes: ruimtes,
      label: (k: KarInBoom) => `${k.ruimte} ${k.ruimteNaam}`.trim(),
    },
    {
      sleutel: 'kar' as const,
      tekst: `${huidige.kar.id} ${huidige.kar.karNaam}`.trim(),
      keuzes: karrenHier,
      label: (k: KarInBoom) =>
        `${`${k.kar.kar} ${k.kar.karNaam}`.trim()} · ${k.kar.onderdelen === 0 ? 'nog geen onderdelen' : k.kar.afdeling}`,
    },
  ];
  // De werkmappen kennen geen decks; dan valt dat niveau weg in plaats van leeg te staan.
  const niveaus = alleNiveaus.filter((n) => n.sleutel !== 'deck' || huidige.deck);

  return (
    <div className="hs-kruimels">
      <span className="hs-mono">{huidige.kar.id.split('-')[0]}</span>
      {niveaus.map((niveau, i) => (
        <span key={niveau.sleutel} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span className="hs-muted-licht">/</span>
          <button
            type="button"
            className={i === niveaus.length - 1 ? 'huidig' : undefined}
            onClick={() => setOpen(niveau.sleutel)}
          >
            {niveau.tekst}
            <IconChevronOnder maat={10} />
          </button>
          {open === niveau.sleutel ? (
            <Dialoog
              titel={niveau.sleutel === 'kar' ? 'Kar kiezen' : `${niveau.sleutel} kiezen`}
              onSluiten={() => setOpen(null)}
              breedte={420}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {niveau.keuzes.map((keuze) => (
                  <button
                    key={keuze.kar.id}
                    type="button"
                    className={`hs-knop${keuze.kar.id === huidige.kar.id ? ' hs-knop--primair' : ''}`}
                    style={{ justifyContent: 'flex-start' }}
                    onClick={() => {
                      onKies(keuze.kar.id);
                      setOpen(null);
                    }}
                  >
                    {niveau.label(keuze)}
                  </button>
                ))}
              </div>
            </Dialoog>
          ) : null}
        </span>
      ))}
    </div>
  );
}

/**
 * Het bouwelement over karren verdelen.
 *
 * De voorbereider zet per onderdeel een karnummer; onderdelen met hetzelfde nummer
 * komen samen op één kar. Dat is de eenvoudigste vorm die werkt met veel onderdelen:
 * geen slepen, wel in één blik te overzien en met de nummers die op de vloer op de
 * karren staan.
 */
function Splitsdialoog({
  bouwnummer,
  karId,
  karNaam,
  elementen,
  onSluiten,
  onKlaar,
}: {
  bouwnummer: string;
  karId: string;
  karNaam: string;
  elementen: Array<{ id: string; code: string; naam: string; onderdelen: number }>;
  onSluiten: () => void;
  onKlaar: () => Promise<void>;
}) {
  const { meldFout } = useGegevens();
  /*
   * Per bouwelement het karnummer waar het op komt. Wie een nieuw nummer intypt, maakt
   * daarmee een nieuwe kar; wat op het huidige nummer blijft staan, verhuist niet. Er
   * wordt per bouwelement verdeeld en niet per onderdeel: een bouwelement staat op
   * precies één kar.
   */
  const [perElement, setPerElement] = useState<Record<string, string>>({});
  const [bezig, setBezig] = useState(false);

  const groepen = useMemo(() => {
    const kaart = new Map<string, string[]>();
    for (const [id, karnummer] of Object.entries(perElement)) {
      const sleutel = karnummer.trim();
      if (!sleutel) continue;
      kaart.set(sleutel, [...(kaart.get(sleutel) ?? []), id]);
    }
    return [...kaart.entries()].sort(([a], [b]) => a.localeCompare(b, 'nl', { numeric: true }));
  }, [perElement]);

  return (
    <Dialoog
      titel={`${karNaam} splitsen`}
      onSluiten={onSluiten}
      breedte={640}
      voet={
        <>
          <button type="button" className="hs-knop" onClick={onSluiten}>
            Annuleren
          </button>
          <button
            type="button"
            className="hs-knop hs-knop--primair"
            disabled={bezig || groepen.length === 0}
            onClick={async () => {
              setBezig(true);
              try {
                await api.splitsen(
                  bouwnummer,
                  karId,
                  groepen.map(([karnummer, bouwelementIds]) => ({ karnummer, bouwelementIds })),
                );
                await onKlaar();
              } catch (e) {
                meldFout(e instanceof Error ? e.message : 'Het splitsen lukte niet.');
              } finally {
                setBezig(false);
              }
            }}
          >
            {groepen.length === 0
              ? 'Niets te verplaatsen'
              : `${groepen.length} ${groepen.length === 1 ? 'nieuwe kar' : 'nieuwe karren'} maken`}
          </button>
        </>
      }
    >
      <p className="hs-muted">
        Vul een karnummer in bij de bouwelementen die van deze kar af moeten. Bouwelementen met
        hetzelfde nummer komen samen op één nieuwe kar; wat je leeg laat blijft staan. Let op: het
        nummer van een bouwelement is zijn plaats, dus wat verhuist krijgt een nieuw nummer.
      </p>

      <table className="hs-tabel">
        <thead>
          <tr>
            <th>Nr</th>
            <th>Bouwelement</th>
            <th className="hs-rechts">Onderdelen</th>
            <th style={{ width: 130 }}>Naar karnummer</th>
          </tr>
        </thead>
        <tbody>
          {elementen.map((element) => (
            <tr key={element.id}>
              <td className="hs-mono">{element.code}</td>
              <td>{element.naam}</td>
              <td className="hs-mono hs-rechts">{element.onderdelen}</td>
              <td>
                <input
                  className="hs-invoer hs-invoer--mono"
                  style={{ width: 110 }}
                  placeholder="blijft staan"
                  value={perElement[element.id] ?? ''}
                  onChange={(e) =>
                    setPerElement((vorig) => ({ ...vorig, [element.id]: e.target.value }))
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {groepen.length > 0 ? (
        <div className="hs-melding hs-melding--info">
          Dit worden {groepen.length} {groepen.length === 1 ? 'nieuwe kar' : 'nieuwe karren'}:{' '}
          {groepen.map(([nr, ids]) => `${nr} (${ids.length})`).join(' · ')}
        </div>
      ) : null}
    </Dialoog>
  );
}

function OnHolddialoog({
  bouwnummer,
  karId,
  karNaam,
  onSluiten,
  onKlaar,
}: {
  bouwnummer: string;
  karId: string;
  karNaam: string;
  onSluiten: () => void;
  onKlaar: () => Promise<void>;
}) {
  const { meldFout } = useGegevens();
  const [reden, setReden] = useState('');
  const [bezig, setBezig] = useState(false);

  return (
    <Dialoog
      titel={`${karNaam} on-hold zetten`}
      onSluiten={onSluiten}
      voet={
        <>
          <button type="button" className="hs-knop" onClick={onSluiten}>
            Annuleren
          </button>
          <button
            type="button"
            className="hs-knop hs-knop--onhold"
            disabled={bezig || reden.trim().length === 0}
            onClick={async () => {
              setBezig(true);
              try {
                await api.onHold(bouwnummer, karId, reden.trim());
                await onKlaar();
              } catch (e) {
                meldFout(e instanceof Error ? e.message : 'On-hold zetten lukte niet.');
              } finally {
                setBezig(false);
              }
            }}
          >
            On-hold zetten
          </button>
        </>
      }
    >
      <p className="hs-muted">
        De stappen van dit bouwelement verdwijnen van de werkvloer en het is niet meer in te plannen. Op
        het dashboard blijft zichtbaar wie het on-hold heeft gezet, wanneer en waarom.
      </p>
      <label className="hs-veld">
        <span>Reden</span>
        <textarea
          className="hs-invoer"
          rows={3}
          autoFocus
          value={reden}
          onChange={(e) => setReden(e.target.value)}
          placeholder="Bijvoorbeeld: wacht op fineerlevering W03."
        />
      </label>
    </Dialoog>
  );
}
